import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

interface LogsResponse {
  logs: string[];
  total: number;
}

const LOG_COLORS: { pattern: RegExp; className: string }[] = [
  { pattern: /\bERROR\b|\bERR\b|\bCRITICAL\b|\bcrit\b|\b❌\b|\b💥\b/i, className: 'text-red-400' },
  { pattern: /\bWARNING\b|\bWARN\b|\b⚠️\b/i,                           className: 'text-yellow-400' },
  { pattern: /\bINFO\b|\b✅\b|\b🚀\b|\b📦\b|\b💬\b|\b🎵\b/i,         className: 'text-green-400' },
  { pattern: /\bDEBUG\b/i,                                               className: 'text-gray-500' },
];

function lineColor(line: string): string {
  for (const { pattern, className } of LOG_COLORS) {
    if (pattern.test(line)) return className;
  }
  return 'text-gray-300';
}

export default function LogsDashboard() {
  const [logs, setLogs]           = useState<string[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [lines, setLines]         = useState(20);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await axios.get<LogsResponse>(`/api/logs?lines=${lines}`);
      setLogs(data.logs || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Erro ao buscar logs');
    } finally {
      setLoading(false);
    }
  }, [lines]);

  // Initial load + interval
  useEffect(() => {
    setLoading(true);
    fetchLogs();
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, [fetchLogs]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  // Detect manual scroll — disable auto-scroll
  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 60;
    setAutoScroll(nearBottom);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            📋 Logs do Bot
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Logs em tempo real do serviço <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">botdobot</code>
            {lastUpdated && (
              <span className="ml-2 text-xs">
                — atualizado às {lastUpdated.toLocaleTimeString('pt-BR')}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Lines selector */}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <label htmlFor="lines-select">Mostrar:</label>
            <select
              id="lines-select"
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:text-gray-200"
            >
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>{n} linhas</option>
              ))}
            </select>
          </div>

          {/* Auto-scroll toggle */}
          <button
            onClick={() => setAutoScroll((v) => !v)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              autoScroll
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {autoScroll ? '⬇ Auto-scroll ON' : '⬇ Auto-scroll OFF'}
          </button>

          {/* Manual refresh */}
          <button
            onClick={fetchLogs}
            className="px-3 py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs flex-wrap">
        <span className="text-green-400 font-mono">■ INFO / sucesso</span>
        <span className="text-yellow-400 font-mono">■ WARNING</span>
        <span className="text-red-400 font-mono">■ ERROR / crítico</span>
        <span className="text-gray-500 font-mono">■ DEBUG</span>
        <span className="text-gray-300 font-mono">■ outros</span>
      </div>

      {/* Terminal box */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="bg-gray-950 rounded-lg border border-gray-700 p-4 h-[60vh] overflow-y-auto font-mono text-xs leading-5 shadow-inner"
        style={{ fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace" }}
      >
        {loading && (
          <div className="text-gray-500 animate-pulse">Carregando logs...</div>
        )}

        {!loading && error && (
          <div className="text-red-400">❌ {error}</div>
        )}

        {!loading && !error && logs.length === 0 && (
          <div className="text-gray-500">Nenhum log disponível.</div>
        )}

        {!loading && !error && logs.map((line, idx) => (
          <div key={idx} className={`whitespace-pre-wrap break-all ${lineColor(line)}`}>
            {line}
          </div>
        ))}

        <div ref={bottomRef} />
      </div>

      {/* Footer hint */}
      <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
        Atualização automática a cada 5 segundos · {logs.length} linhas exibidas
      </p>
    </div>
  );
}
